import { useState } from 'react';
import { AccountType } from 'further-types/investor';
import { canResetKyc } from 'further-ui/utils';
import {
  OnboardingStep,
  OnboardingStepStatus,
} from 'further-types/lp/onboarding';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { getQueryKey } from '@trpc/react-query';
import { KycProcessStatus } from 'further-types/kyc';

export default function useReviewActions(
  vehicleApplicationId: string,
  vehicleId: string,
  step: {
    key: OnboardingStep;
    canUnapprove: boolean;
    status?: OnboardingStepStatus | null;
  },
) {
  const [
    isRequestMoreInformationFormOpen,
    setIsRequestMoreInformationFormOpen,
  ] = useState(false);

  const isApproved = step.status === OnboardingStepStatus.Completed;
  const {
    approveStep,
    unapproveStep,
    requestMoreInformation,
    investor,
    isPending: isApproveOrRequestMoreInformationPending,
    queryKey,
  } = useVehicleApplication(vehicleId, vehicleApplicationId);

  const { data: investorKyc } = api.lpKyc.getInvestorKyc.useQuery(
    {
      vehicleId,
      investorId: investor.id,
    },
    {
      enabled: step.key === OnboardingStep.KYCOrKYB,
    },
  );

  const { mutate: resetStep, isPending: isResetStepPending } =
    api.vehicleApplication.resetApplicationStep.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Step reset successfully.',
        invalidations: [
          queryKey,
          getQueryKey(api.lpInvestmentCoolOff.get),
          getQueryKey(api.investorClassification.getClassificationResult, {
            vehicleId,
            vehicleApplicationId,
          }),
          getQueryKey(api.lpKyc.getKycDetails, {
            vehicleId,
            vehicleApplicationId,
          }),
          getQueryKey(api.lpKyc.getKybDetails, {
            vehicleId,
            vehicleApplicationId,
          }),
          getQueryKey(api.lpKyc.getInvestorKyc, {
            vehicleId,
            investorId: investor.id,
          }),
          getQueryKey(
            api.investorClassification.listExpiredClassificationResults,
            {
              vehicleId,
              vehicleApplicationId,
            },
          ),
          getQueryKey(api.investorTaxDetails.listExpiredTaxDetails, {
            vehicleId,
            vehicleApplicationId,
          }),
        ],
      }),
    );

  const isPending =
    isApproveOrRequestMoreInformationPending || isResetStepPending;

  const handleApproveUnapprove = () => {
    if (isPending) {
      return;
    }

    if (isApproved && step.canUnapprove) {
      unapproveStep({
        vehicleId,
        vehicleApplicationId,
        onboardingStep: step.key,
      });
    } else if (!isApproved) {
      approveStep({
        vehicleId,
        vehicleApplicationId,
        onboardingStep: step.key,
      });
    }
  };

  const openRequestMoreInformationForm = () => {
    setIsRequestMoreInformationFormOpen(true);
  };

  const closeRequestMoreInformationForm = () => {
    setIsRequestMoreInformationFormOpen(false);
  };

  const handleRequestMoreInformation = (message: string) => {
    if (isPending || isApproved) {
      return;
    }

    requestMoreInformation(
      {
        vehicleId,
        vehicleApplicationId,
        onboardingStep: step.key,
        message,
      },
      {
        onSuccess: () => {
          closeRequestMoreInformationForm();
        },
      },
    );
  };

  const handleResetStep = () => {
    if (isPending) {
      return;
    }

    resetStep({
      vehicleApplicationId,
      vehicleId,
      step: step.key,
    });
  };

  const showRequestMoreInformationButton =
    step.status === OnboardingStepStatus.InReview;

  const showResetKyc =
    step.key === OnboardingStep.KYCOrKYB &&
    canResetKyc(investorKyc ?? undefined);

  const showResetStepButton =
    (step.status === OnboardingStepStatus.Completed &&
      [
        OnboardingStep.InvestorClassification,
        OnboardingStep.TaxDeclarations,
      ].includes(step.key)) ||
    showResetKyc;

  const isKycManuallyApproved =
    investorKyc?.idvProcessStatus === KycProcessStatus.ManuallyApproved;

  const showManualKycButton =
    step.key === OnboardingStep.KYCOrKYB &&
    (step.status !== OnboardingStepStatus.Completed || isKycManuallyApproved);
  const hasManualKyc =
    !!investorKyc?.manualApprovalNotes ||
    !!investorKyc?.manualApprovalFiles?.length;

  const showApproveButton =
    step.status === OnboardingStepStatus.InReview ||
    step.status === OnboardingStepStatus.MoreInformationRequested ||
    step.status === OnboardingStepStatus.Completed ||
    (step.key === OnboardingStep.KYCOrKYB && hasManualKyc);

  const kycMode =
    investor.accountType === AccountType.Individual ? 'KYC' : 'KYB';

  const manualKycButtonLabel = hasManualKyc
    ? `Edit manual ${kycMode}`
    : `Upload manual ${kycMode}`;

  const approveButtonLabel = isApproved
    ? step.key === OnboardingStep.KYCOrKYB && isKycManuallyApproved
      ? `Approved with manual ${kycMode}`
      : 'Approved'
    : 'Approve';
  return {
    showResetStepButton,
    showManualKycButton,
    showRequestMoreInformationButton,
    manualKycButtonLabel,
    approveButtonLabel,
    isApproved,
    handleApproveUnapprove,
    handleResetStep,
    handleRequestMoreInformation,
    isApproveOrRequestMoreInformationPending,
    isPending,
    showApproveButton,
    isRequestMoreInformationFormOpen,
    openRequestMoreInformationForm,
    closeRequestMoreInformationForm,
  };
}
